import Vue from 'vue'
import { onSignalrConnectionCreated } from '@signalrConnection'
import store from '@/store'
import router, { userRolesAuthorizedPaths } from '@/router'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const YOUNGS_STORE_MODULE_NAME = 'youngs'

const MESSAGES_ROUTE_PATH = '/jeunes'

const SENDER_TYPE_YOUNG = 1

const SMS_OR_WHATSAPP_MESSAGE_TYPES = [1, 2]

function canCurrentUserReceiveMessageNotifications() {
  const userRole = store.getters['users/getUserRole']
  if (!userRole || !userRolesAuthorizedPaths[userRole]?.includes(MESSAGES_ROUTE_PATH)) {
    return false
  }

  // "Messages" permission business rule (mirrors `hasSmsPermission` in Youngs.vue and the
  // badge-tracking rule in `youngsStoreModule.js`): only Coordinateurs/\u00c9coutants with
  // `canSendMessage` enabled should ever be interrupted with a new-message popup. Read fresh
  // from the store on every event so it always reflects the CURRENTLY authenticated user,
  // even right after switching accounts.
  const currentUser = store.getters['users/currentUser']
  const roles = currentUser?.roles || []
  const hasEligibleRole = roles.includes('LeRefuge.Coordinateur') || roles.includes('LeRefuge.Ecoutant')
  return hasEligibleRole && currentUser?.profile?.canSendMessage === true
}

// The backend already scopes the SignalR event to users allowed to *view* the conversation
// (Coordinateurs + the relevant Écoutants — see TwilioService.SendNotificationsAsync). On top
// of that, the popup itself is further restricted to the person actually *responsible* for
// this young (`message.userId`, populated from `Young.WhoTakesCareUserId`), so it isn't popped
// up for the whole team. Unassigned conversations intentionally don't trigger a popup here;
// they remain visible in the Youngs list for whoever picks them up.
function isCurrentUserResponsibleForYoung(message) {
  const currentUserId = store.getters['auth/getUserId']
  return !!currentUserId && !!message.userId && message.userId === currentUserId
}

// Avoid interrupting the user: don't pop a toast for a conversation they are already looking at.
function isAlreadyViewingConversation(youngId) {
  const youngsState = store.state[YOUNGS_STORE_MODULE_NAME]

  return router.currentRoute.path === MESSAGES_ROUTE_PATH
    && !!youngsState
    && youngsState.activeYoungId === youngId
}

function getYoungDisplayName(youngId) {
  const young = store.state[YOUNGS_STORE_MODULE_NAME]?.youngs?.find(y => y.id === youngId)

  if (!young) {
    return 'Un·e jeune'
  }

  return `${young.firstName || ''} ${(young.lastName || '').toUpperCase()}`.trim() || 'Un·e jeune'
}

function showNewMessageToast(message) {
  Vue.prototype.$toast(
    {
      component: ToastificationContent,
      props: {
        title: 'Nouveau message',
        icon: 'MessageSquareIcon',
        variant: 'info',
        text: `${getYoungDisplayName(message.youngId)} vous a envoyé un nouveau message.`,
      },
    },
    {
      timeout: 6000,
      onClick: () => {
        router.push({ name: 'youngs', query: { youngId: message.youngId } }).catch(() => {})
      },
    },
  )
}

function notifyIfRelevant(message) {
  if (!message || message.senderType !== SENDER_TYPE_YOUNG) {
    return
  }

  // Exclude call events (missed/successful calls) that are also broadcast via 'ReceiveMessage'.
  if (!SMS_OR_WHATSAPP_MESSAGE_TYPES.includes(message.type)) {
    return
  }

  if (!canCurrentUserReceiveMessageNotifications()) {
    return
  }

  if (!isCurrentUserResponsibleForYoung(message)) {
    return
  }

  if (isAlreadyViewingConversation(message.youngId)) {
    return
  }

  showNewMessageToast(message)
}

let registeredConnection = null

/**
 * Registers a single, app-wide set of SignalR listeners for the Youngs/messaging feature.
 *
 * This replaces the previous per-page registration (`useYoungsSocket.js`) that used to run
 * `signalrConnection.on(...)` on mount and `signalrConnection.off(eventName)` on unmount.
 * That pattern was fragile: `HubConnection.off(eventName)` (without a specific callback
 * reference) removes *every* handler registered for that event name — including ones
 * registered elsewhere — so leaving and re-entering the Youngs page could silently wipe out
 * other subscribers (e.g. this notifier) for the rest of the session.
 *
 * Registering once, for the lifetime of the app, and never calling `.off(...)`, avoids that
 * class of bug entirely and also means the Youngs store (list/unread counts) and the new
 * message popup both keep working no matter which page is currently displayed.
 *
 * Safe to call multiple times; registration only happens once.
 */
function registerRealtimeMessagingHandlers(signalrConnection) {
  if (!signalrConnection || registeredConnection === signalrConnection) {
    return
  }

  signalrConnection.on('ReceiveMessage', message => {
    store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/addOrUpdateMessage`, message)
    notifyIfRelevant(message)
  })

  signalrConnection.on('ReceiveYoung', young => {
    store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/addOrUpdateYoung`, young)
  })

  signalrConnection.on('DeleteYoung', youngId => {
    store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/deleteYoung`, youngId)
  })

  signalrConnection.on('ReceiveConversationStatus', takeCare => {
    store.commit(`${YOUNGS_STORE_MODULE_NAME}/updateConversationStatus`, {
      youngId: takeCare.youngId,
      conversationStatus: takeCare.conversationStatus,
      userId: takeCare.userId,
    })
  })

  registeredConnection = signalrConnection
}

export default function registerRealtimeMessaging() {
  onSignalrConnectionCreated(registerRealtimeMessagingHandlers)
}
